import { cn } from "@/lib/cn";

/**
 * HorizontalBarChart — a clean, editorial bar chart for demographic and
 * behavioral data. Each row renders as: label · bar · percentage. Bars
 * are scaled against the largest value in the dataset so the visual
 * comparison is immediate.
 *
 * Usage:
 *   <HorizontalBarChart
 *     data={[{ label: "25–34", value: 25 }, ...]}
 *     labelClassName="w-24"
 *   />
 */
export const HorizontalBarChart = ({
  data = [],
  labelClassName = "w-24",
  testId,
  ariaLabel,
}) => {
  if (!data.length) return null;
  const max = Math.max(...data.map((d) => d.value));

  return (
    <div
      data-testid={testId || "bar-chart"}
      role="img"
      aria-label={ariaLabel}
      className="flex flex-col gap-4"
    >
      {data.map((d) => {
        const pct = max === 0 ? 0 : (d.value / max) * 100;
        return (
          <div
            key={d.label}
            data-testid={`bar-row-${d.label.replace(/\s+/g, "-").toLowerCase()}`}
            className="flex items-center gap-4"
          >
            <div
              className={cn(
                "shrink-0 text-sm text-muted-foreground sm:text-[15px]",
                labelClassName
              )}
            >
              {d.label}
            </div>
            <div className="relative h-[6px] flex-1 overflow-hidden rounded-full bg-border">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-foreground/80 transition-[width] duration-700 ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="w-12 shrink-0 text-right text-sm font-medium tabular-nums text-foreground sm:text-[15px]">
              {d.value}%
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default HorizontalBarChart;
